import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { seekAds, hireRequests } from '../utils/api';
import SeekAdCard from '../components/SeekAdCard';
import Modal from '../components/Modal';
import toast from 'react-hot-toast';

const CATEGORIES = ['All','Technical','Domestic','Freelance','Tutor','Creative','Local Task'];
const RATE_TYPES  = ['All','HOURLY','DAILY','WEEKLY','MONTHLY','FIXED'];

import LocationSelector from '../components/LocationSelector';

export default function FindHire() {

  const { t, user } = useApp();
  const navigate     = useNavigate();
  const [searchParams] = useSearchParams();
  const [ads,     setAds]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState(searchParams.get('q') || '');
  const [cat,     setCat]     = useState(searchParams.get('category') || 'All');
  const [rate,    setRate]    = useState('All');
  const [loc,     setLoc]     = useState(searchParams.get('location') || '');
  
  // Advanced filters
  const [minRate, setMinRate] = useState('');
  const [maxRate, setMaxRate] = useState('');
  const [orderBy, setOrderBy] = useState('recent');
  const [showFilters, setShowFilters] = useState(false);

  const [hireAd,  setHireAd]  = useState(null);
  const [hireMsg, setHireMsg] = useState('');
  const [hiring,  setHiring]  = useState(false);

  const fetchAds = useCallback(() => {
    setLoading(true);
    const params = {};
    if (search)         params.q        = search;
    if (cat  !== 'All') params.category = cat;
    if (rate !== 'All') params.rateType = rate;
    if (loc)            params.location = loc;
    
    if (minRate) params.min_rate = minRate;
    if (maxRate) params.max_rate = maxRate;
    if (orderBy) params.order_by = orderBy;

    seekAds.list(params)
      .then(r => setAds(r.seekAds ?? []))
      .catch(() => toast.error(t('error_occurred')))
      .finally(() => setLoading(false));
  }, [search, cat, rate, loc, minRate, maxRate, orderBy, t]);

  useEffect(() => { fetchAds(); }, [fetchAds]);

  const handleContact = async ad => {
    if (!user) { navigate('/login'); return; }
    try {
      await seekAds.unlockContact(ad.id);
      toast.success('Contact unlocked!');
      navigate(`/seek-ads/${ad.id}`);
    } catch (err) { toast.error(err.error || t('error_occurred')); }
  };

  const handleHireSend = async () => {
    if (!hireAd) return;
    setHiring(true);
    try {
      await seekAds.sendHireRequest(hireAd.id, { message: hireMsg });
      toast.success(t('hire_request_sent'));
      setHireAd(null);
      setHireMsg('');
    } catch (err) { toast.error(err.error || t('error_occurred')); }
    finally { setHiring(false); }
  };

  return (
    <div className="container" style={{ paddingTop: '32px', paddingBottom: '60px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '26px', fontWeight: 700, marginBottom: '6px' }}>{t('nav_find_hire')}</h1>
        <p style={{ color: 'var(--text3)', fontSize: '14px' }}>
          Browse job seekers who published their skills and rate. Send a hire request directly.
        </p>
      </div>

      {/* Filters Section */}
      <div className="card" style={{ marginBottom: '24px', padding: '20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '12px', marginBottom: '16px' }}>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)' }}>🔍</span>
            <input 
              className="form-input"
              style={{ paddingLeft: '36px' }}
              placeholder={t('search_hire_placeholder')} 
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && fetchAds()} 
            />
          </div>
          <button 
            className={`btn ${showFilters ? 'btn-primary' : 'btn-outline'}`} 
            onClick={() => setShowFilters(!showFilters)}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <span>⚙️</span> {t('filters')}
          </button>
          <button className="btn btn-primary" onClick={fetchAds}>{t('search_btn')}</button>
        </div>

        {/* Basic Filters */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: showFilters ? '16px' : '0' }}>
          <div>
            <label className="form-label" style={{ fontSize: '12px' }}>{t('category')}</label>
            <select className="form-input" value={cat} onChange={e => setCat(e.target.value)}>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label" style={{ fontSize: '12px' }}>{t('location')}</label>
            <LocationSelector initialValue={loc} onChange={val => setLoc(val)} />
          </div>
          <div>
            <label className="form-label" style={{ fontSize: '12px' }}>{t('order_by')}</label>
            <select className="form-input" value={orderBy} onChange={e => setOrderBy(e.target.value)}>
              <option value="recent">{t('recent')}</option>
              <option value="oldest">{t('oldest')}</option>
              <option value="rate_desc">{t('rate_desc')}</option>
              <option value="rate_asc">{t('rate_asc')}</option>
            </select>
          </div>
        </div>

        {showFilters && (
          <div className="stagger" style={{ borderTop: '1px solid var(--border)', paddingTop: '16px', marginTop: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
              <div>
                <label className="form-label" style={{ fontSize: '12px' }}>{t('rate_type')}</label>
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  {RATE_TYPES.map(r => (
                    <button key={r} onClick={() => setRate(r)}
                      className={`chip ${rate === r ? 'chip-teal' : 'chip-gray'}`}
                      style={{ cursor: 'pointer', border: 'none', padding: '4px 10px', fontSize: '11px' }}>
                      {r === 'All' ? 'All' : r}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="form-label" style={{ fontSize: '12px' }}>{t('rate')} (৳)</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input className="form-input" type="number" placeholder="Min" value={minRate} onChange={e => setMinRate(e.target.value)} style={{ padding: '6px 10px', fontSize: '13px' }} />
                  <span style={{ color: 'var(--text3)' }}>-</span>
                  <input className="form-input" type="number" placeholder="Max" value={maxRate} onChange={e => setMaxRate(e.target.value)} style={{ padding: '6px 10px', fontSize: '13px' }} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      {loading ? (
        <div className="page-loader"><div className="spinner" /></div>
      ) : ads.length === 0 ? (
        <div className="empty-state">
          <div style={{ fontSize: '40px', marginBottom: '10px' }}>🔍</div>
          <p>{t('no_seekers_found')}</p>
        </div>
      ) : (
        <div>
          <p style={{ fontSize: '13px', color: 'var(--text3)', marginBottom: '12px' }}>
            {ads.length} seeker{ads.length !== 1 ? 's' : ''} available
          </p>
          <div className="stagger">
            {ads.map(ad => (
              <SeekAdCard key={ad.id} ad={ad}
                onHire={ad => { if (!user) { navigate('/login'); return; } setHireAd(ad); setHireMsg(''); }}
                onContact={handleContact} />
            ))}
          </div>
        </div>
      )}

      {/* Hire modal */}
      <Modal open={!!hireAd} onClose={() => setHireAd(null)} title={`Hire ${hireAd?.seekerName}`}>
        <div style={{ marginBottom: '14px', padding: '12px', background: 'var(--surface2)', borderRadius: 'var(--radius)' }}>
          <div style={{ fontWeight: 600, marginBottom: '4px' }}>{hireAd?.title}</div>
          <div style={{ fontSize: '13px', color: 'var(--text3)' }}>
            ৳{Number(hireAd?.rate ?? 0).toLocaleString()} / {hireAd?.rateType?.toLowerCase()}
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Message to seeker (optional)</label>
          <textarea className="form-input" rows={4} value={hireMsg}
            onChange={e => setHireMsg(e.target.value)}
            placeholder="Introduce yourself, describe the work, mention start date..."
            style={{ resize: 'vertical' }} />
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn btn-outline" onClick={() => setHireAd(null)}>Cancel</button>
          <button className="btn btn-primary btn-full" onClick={handleHireSend} disabled={hiring}>
            {hiring ? <span className="spinner" style={{ width: 16, height: 16 }} /> : t('send_hire_request')}
          </button>
        </div>
      </Modal>
    </div>
  );
}
